import React from 'react';
import { useTranslation } from 'react-i18next';

import { Formik, FormikHelpers } from 'formik';

import { IClientInstrumentStatusForm } from 'api/clients/types';
import Button from 'components/Button';
import { appReactMemo } from 'hocs';
import { useGetDictionaryNaming } from 'hooks/useGetDictionaryNaming';
import { ScrollToFirstError } from 'hooks/useScrollToFirstError';
import { useEditClientInstrument } from 'queries/clients';
import { useGetDictionaries } from 'queries/data';
import { getFormErrors, getFormTouched } from 'utils/common';
import { INullable, QueryError } from 'utils/types';

import {
  ButtonContainer,
  PopoverContainer,
  StyledForm,
  StyledRadioSelect,
} from './styled';

interface IChangeInstrumentStatus {
  cardPanId: INullable<number>;
  customerId: INullable<number>;
  verification: INullable<string>;
}

const ChangeInstrumentStatus = ({
  cardPanId,
  customerId,
  verification,
}: IChangeInstrumentStatus) => {
  const { t } = useTranslation();

  const { data: appData } = useGetDictionaries();
  const getDictionaryNaming = useGetDictionaryNaming();

  const { mutate: editClientInstrument } = useEditClientInstrument();

  const onSubmit = React.useCallback(
    (
      payload: IClientInstrumentStatusForm,
      helpers: FormikHelpers<IClientInstrumentStatusForm>,
    ) => {
      editClientInstrument(payload, {
        onError: (resp: QueryError) => {
          helpers.setTouched(getFormTouched(resp?.response?.data?.errors), false);
          helpers.setErrors(
            getFormErrors({ errors: resp?.response?.data?.errors, getTranslation: t }),
          );
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      });
    },
    [editClientInstrument],
  );

  const initialValues: IClientInstrumentStatusForm = React.useMemo(() => {
    return {
      cardPanId,
      customerId,
      verification,
    };
  }, [cardPanId, customerId, verification]);

  const statusOption = React.useMemo(
    () =>
      appData?.cardTokenVerificationStatus?.map((type) => ({
        label: getDictionaryNaming('cardTokenVerificationStatus', type),
        value: type,
      })) || [],
    [appData?.cardTokenVerificationStatus, getDictionaryNaming],
  );

  return (
    <PopoverContainer>
      <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
        {({ dirty, isSubmitting }) => (
          <>
            <ScrollToFirstError />
            <StyledForm>
              <StyledRadioSelect
                data={statusOption}
                label={t('clientsInstruments.status')}
                name='verification'
              />
              <ButtonContainer>
                <Button
                  disabled={isSubmitting || !dirty}
                  isLoading={isSubmitting}
                  type='submit'
                  variant='primary'
                >
                  {t('common.save')}
                </Button>
              </ButtonContainer>
            </StyledForm>
          </>
        )}
      </Formik>
    </PopoverContainer>
  );
};

export default appReactMemo(ChangeInstrumentStatus);
